import {
  useMutation,
  useQuery,
  useQueryClient,
  type QueryClient,
  type UseMutationOptions,
  type UseQueryOptions,
} from "@tanstack/react-query";
import { api, withQuery } from "./api";

type Params = Parameters<typeof withQuery>[1];

/** A string invalidates that path and everything under it (`/experiments` also
 *  hits `/experiments/e1/items`); a RegExp matches the path of a query; a key
 *  array is TanStack's usual partial match (`["/connections"]` hits the list
 *  but not `/connections/c1/groups`). */
export type Invalidate = string | RegExp | readonly unknown[];

const under = (head: unknown, path: string) =>
  head === path || (typeof head === "string" && head.startsWith(`${path}/`));

export function invalidatePaths(queryClient: QueryClient, targets: readonly Invalidate[]) {
  return Promise.all(
    targets.map((target) =>
      typeof target === "string" || target instanceof RegExp
        ? queryClient.invalidateQueries({
            predicate: ({ queryKey: [head] }) =>
              typeof target === "string"
                ? under(head, target)
                : typeof head === "string" && target.test(head),
          })
        : queryClient.invalidateQueries({ queryKey: target }),
    ),
  );
}

/** GET `path` with optional query-string params, cached under `[path, params]`.
 *  Pass `enabled: false` until every part of the path is known. */
export function useApiQuery<T>(
  path: string,
  params: Params = {},
  options: Omit<UseQueryOptions<T, Error, T, readonly unknown[]>, "queryKey" | "queryFn"> = {},
) {
  return useQuery({
    queryKey: [path, params],
    queryFn: () => api.get<T>(withQuery(path, params)),
    ...options,
  });
}

/** Send the mutation variables as the body (none for DELETE) and, on success,
 *  refetch whatever `invalidate` names. When `path` is a function the variables
 *  are only its argument (an id) and no body goes out unless `body` picks one. */
export function useApiMutation<T = void, V = void>(
  method: "post" | "put" | "patch" | "delete",
  path: string | ((variables: V) => string),
  {
    invalidate = [],
    body,
    ...options
  }: { invalidate?: readonly Invalidate[]; body?: (variables: V) => unknown } & Omit<
    UseMutationOptions<T, Error, V>,
    "mutationFn"
  > = {},
) {
  const queryClient = useQueryClient();
  return useMutation<T, Error, V>({
    ...options,
    mutationFn: (variables) => {
      const url = typeof path === "function" ? path(variables) : path;
      if (method === "delete") return api.delete(url) as Promise<T>;
      const payload = body ? body(variables) : typeof path === "function" ? undefined : variables;
      return api[method]<T>(url, payload);
    },
    onSuccess: async (...args) => {
      await options.onSuccess?.(...args);
      await invalidatePaths(queryClient, invalidate);
    },
  });
}
